import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router';
import { z } from 'zod';
import { api } from '../../api/client';
import { errorMessageKey } from '../../api/api-error';
import { captchaToken } from '../../auth/captcha';
import { ensureOk, useAuthOptions } from '../../auth/session';
import { Button } from '../../components/ui/button';
import { AuthError, AuthLayout, AuthLinks } from '../../components/auth/auth-dialog';
import { Notice } from '../../components/ui/notice';
import { TextField } from '../../components/ui/text-field';
import { useI18n } from '../../i18n/i18n';

const schema = z.object({
  email: z.string().trim().min(1, 'validation.required').pipe(z.email('validation.email')),
});
type Values = z.infer<typeof schema>;

export function ForgotPasswordPage() {
  const { t } = useI18n();
  const options = useAuthOptions();
  const form = useForm<Values>({ resolver: zodResolver(schema), defaultValues: { email: '' } });
  const request = useMutation({
    mutationFn: async (values: Values) => {
      ensureOk(
        await api.POST('/api/v1/auth/password/forgot', {
          body: {
            ...values,
            captchaToken: await captchaToken(
              options.data?.recaptchaSiteKey ?? null,
              'password_reset',
            ),
          },
        }),
      );
    },
  });

  return (
    <AuthLayout title={t('auth.forgot.title')}>
      {request.isSuccess ? (
        // Тот же текст, есть такой email или нет: ответ сервера одинаковый.
        <Notice tone="success">{t('auth.forgot.sent')}</Notice>
      ) : (
        <form
          className="space-y-4"
          onSubmit={form.handleSubmit((values) => request.mutate(values))}
          noValidate
        >
          <p className="text-sm text-muted-foreground">{t('auth.forgot.hint')}</p>
          <TextField
            control={form.control}
            name="email"
            type="email"
            label={t('auth.field.email')}
            autoComplete="email"
            autoFocus
          />
          {request.isError && <AuthError>{t(errorMessageKey(request.error))}</AuthError>}
          <Button type="submit" className="w-full" disabled={request.isPending}>
            {t('auth.forgot.submit')}
          </Button>
        </form>
      )}
      <AuthLinks>
        <Link to="/login">{t('auth.toLogin')}</Link>
      </AuthLinks>
    </AuthLayout>
  );
}
